import React from 'react'
import type { ReactNode } from 'react'
import _ from 'lodash'

import { VEHICLE_NUMBER_LENGTH } from 'constants/Booking'
import * as S from '../style'
import Header from './Header'
import PrevStartButton from './PrevStartButton'
import PageNumber from './PageNumber'
import { SpFooter } from './SpFooter'
import { DRIVER_NAME_GENERAL_ID } from '../constants'
import type { GeneralBookingType } from '../type'
import type useEntryDriverInfoPage from '../hooks/useEntryDriverInfoPage'
import styles from './EntryDriverInfoSpView.module.css'

type Controller = ReturnType<typeof useEntryDriverInfoPage>

export type Props = {
  booking: GeneralBookingType
  isDisabledNextButton: boolean
  existsDriverConfirmations: boolean
  isFreePlan: boolean
  listCompanyOptions: Controller['listCompanyOptions']
  onClickExit: () => void
  onClickBack: Controller['handleClickButtonBack']
  onClickNext: () => void
  onChangeDriverCompany: Controller['handleChangeDriverCompany']
  onBlurDriverCompany: Controller['handleBlurDriverCompany']
  onInputChangeDriverCompany: (value: string) => string
  onChangeDriverName: Controller['handleChangeDriverName']
  onFocusDriverName: Controller['handleFocusInputDriverName']
  onOpenVehicleNumberModal: () => void
  onChangeVehicleCategory: Controller['handleSelectVehicleCategory']
  renderVehicleOptions: Controller['renderVehicleOptions']
  modals: ReactNode
}

// 総合受付 運転手情報入力画面の SP 表示。カード内にフィールドを縦積みし、車番＋車両形態のみ 2 カラム。
export const EntryDriverInfoSpView = ({
  booking,
  isDisabledNextButton,
  existsDriverConfirmations,
  isFreePlan,
  listCompanyOptions,
  onClickExit,
  onClickBack,
  onClickNext,
  onChangeDriverCompany,
  onBlurDriverCompany,
  onInputChangeDriverCompany,
  onChangeDriverName,
  onFocusDriverName,
  onOpenVehicleNumberModal,
  onChangeVehicleCategory,
  renderVehicleOptions,
  modals,
}: Props) => (
  <S.Wrapper>
    <Header />
    <div className={styles.body}>
      <div className={styles.back}>
        <PrevStartButton
          onClick={onClickExit}
          data-dg-id="sp-entry-driver-info-page-prev-start-button"
        />
      </div>

      <div className={styles.card}>
        <div className={styles.heading}>
          <div className={styles.title}>ドライバー情報を入力</div>
        </div>

        <div className={styles.fields}>
          <div className={styles.field}>
            <div
              className={styles.label}
              data-dg-id="sp-entry-driver-info-page-label-driver-company-name"
            >
              <span
                className={styles.required}
                data-dg-id="sp-entry-driver-info-page-required-driver-company-name"
              >
                必須
              </span>
              運送会社名
            </div>
            <div
              className={styles.companyInput}
              data-dg-id="sp-entry-driver-info-page-input-driver-company-name"
            >
              <S.CustomCreatableSelect
                isClearable
                isInputDisplay
                options={listCompanyOptions}
                value={{
                  value: booking.driverCompanyName,
                  label: booking.driverCompanyName,
                }}
                onChange={onChangeDriverCompany}
                onBlur={onBlurDriverCompany}
                height="45px"
                fontSize="14px"
                onInputChange={onInputChangeDriverCompany}
              />
            </div>
          </div>

          <div className={styles.field}>
            <div className={styles.label} data-dg-id="sp-entry-driver-info-page-label-driver-name">
              <span
                className={styles.required}
                data-dg-id="sp-entry-driver-info-page-required-driver-name"
              >
                必須
              </span>
              氏名フルネーム
            </div>
            <input
              id={DRIVER_NAME_GENERAL_ID}
              type="text"
              className={styles.input}
              value={booking.driverName}
              onChange={onChangeDriverName}
              onFocus={onFocusDriverName}
              placeholder="例：山田　太郎"
              maxLength={50}
              data-dg-id="sp-entry-driver-info-page-input-driver-name"
            />
          </div>

          <div className={styles.row2}>
            <div className={styles.field}>
              <div
                className={styles.label}
                data-dg-id="sp-entry-driver-info-page-label-vehicle-number"
              >
                <span
                  className={styles.required}
                  data-dg-id="sp-entry-driver-info-page-required-vehicle-number"
                >
                  必須
                </span>
                車番
              </div>
              <input
                type="text"
                className={styles.input}
                value={booking.vehicleNumber}
                maxLength={VEHICLE_NUMBER_LENGTH}
                onClick={onOpenVehicleNumberModal}
                onChange={_.noop}
                readOnly
                data-dg-id="sp-entry-driver-info-page-input-vehicle-number"
              />
            </div>
            <div className={styles.field}>
              <div
                className={styles.label}
                data-dg-id="sp-entry-driver-info-page-label-vehicle-category"
              >
                <span
                  className={styles.required}
                  data-dg-id="sp-entry-driver-info-page-required-vehicle-category"
                >
                  必須
                </span>
                車両形態
              </div>
              <select
                className={styles.input}
                value={booking.vehicleCategory}
                onChange={onChangeVehicleCategory}
                data-dg-id="sp-entry-driver-info-page-input-vehicle-category"
              >
                {renderVehicleOptions()}
              </select>
            </div>
          </div>
        </div>
      </div>

      <SpFooter
        onBack={onClickBack}
        onNext={onClickNext}
        isNextDisabled={isDisabledNextButton}
        backDgId="sp-entry-driver-info-page-back-button"
        nextDgId={
          isDisabledNextButton
            ? 'sp-entry-driver-info-page-next-button-disabled'
            : 'sp-entry-driver-info-page-next-button'
        }
      />

      <div className={styles.pageNumber}>
        <PageNumber
          page="entry-driver-info"
          existsDriverConfirmations={existsDriverConfirmations}
          isFreePlan={isFreePlan}
        />
      </div>
    </div>
    {modals}
  </S.Wrapper>
)
